// src/services/apiClient.js

// API Base URL — ใช้ relative URL ("") เพื่อให้ทำงานได้บนทั้ง localhost และ production
// Dev  (npm start): CRA proxy ใน package.json ส่งต่อ /api/* → http://localhost:5000
// Prod (npm build): Express serve ทั้ง frontend + API บน domain เดียวกัน → /api/* ทำงานตรง
// ถ้าต้องการ override ให้ตั้ง REACT_APP_API_BASE=https://... ใน .env.development.local
export const API_BASE = process.env.REACT_APP_API_BASE || "";

// Link to a file the backend serves (/uploads/…, /pdfs/…). A bare "/uploads/x.png" would open on the
// frontend dev server (:3000) and show the React app instead of the file, so prefix the API origin.
export function fileHref(url) {
  const u = String(url || "");
  if (!u) return "";
  if (/^(https?:|blob:|data:)/i.test(u)) return u;
  return `${API_BASE}${u.startsWith("/") ? "" : "/"}${u}`;
}

// Admin session is an HttpOnly cookie. Same-origin requests (production) send it
// automatically; when API_BASE points to another origin (local dev :3000 → :5000)
// fetch needs credentials: "include". Patch fetch once so every existing
// `${API_BASE}/api/...` call sends the cookie without editing each page.
export function enableApiCredentials() {
  if (!API_BASE || typeof window === "undefined" || window.__mngFetchPatched) return;
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const url = typeof input === "string" ? input : input?.url || "";
    if (url.startsWith(API_BASE) && !init.credentials) {
      return originalFetch(input, { ...init, credentials: "include" });
    }
    return originalFetch(input, init);
  };
  window.__mngFetchPatched = true;
}

async function request(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...options,
    headers: {
      ...(options.headers || {}),
    },
  });

  // กันกรณี response ไม่ใช่ JSON
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = data?.message || data?.error || `HTTP ${res.status}`;
    throw Object.assign(new Error(msg), { status: res.status });
  }

  return data;
}

// ======= Announcement APIs =======
export function uploadAnnouncementImage({ fileOrBlob, filename = "announcement.png", issueDate, title }) {
  const fd = new FormData();
  fd.append("image", fileOrBlob, filename);
  fd.append("issueDate", issueDate);
  if (title) fd.append("title", title);

  return request("/api/announcement/upload", {
    method: "POST",
    body: fd,
  });
}

// Overwrite an announcement's image file (same file name, so existing links keep working)
export function replaceAnnouncementImage(id, blob, filename = "announcement.png") {
  const fd = new FormData();
  fd.append("image", blob, filename);
  return request(`/api/announcement/${encodeURIComponent(id)}/image`, {
    method: "PUT",
    body: fd,
  });
}

export function getImportedAnnouncement(importId) {
  return request(`/api/excel/imported/${encodeURIComponent(importId)}`);
}

export function getAnnouncementsByDate(date) {
  return request(`/api/announcements/by-date?date=${encodeURIComponent(date)}`);
}

export function deleteAnnouncement(id) {
  return request(`/api/announcement/${id}`, { method: "DELETE" });
}

export function updateAnnouncementIssueDate(id, issueDate) {
  return request(`/api/announcement/${id}/issue-date`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ issueDate }),
  });
}

// ======= Admin Announcement CRUD =======
export function listAnnouncements() {
  return request("/api/announcements");
}

export function getAnnouncementById(id) {
  return request(`/api/announcement/${encodeURIComponent(id)}`);
}

export function createAnnouncement(payload) {
  return request("/api/announcement", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateAnnouncement(id, payload) {
  return request(`/api/announcement/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}


// ======= Daily PDF APIs =======
export function generateDailyPdf(date) {
  return request("/api/daily-pdf/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ date }),
  });
}

export function listDailyPdfs() {
  return request("/api/daily-pdf/list");
}

// ======= Bulk Import APIs (Phase 3) =======

/**
 * ตรวจสอบข้อมูลกับ backend ก่อน import
 * @param {{ schemaVersion: string, announcementType: string, sourceFileName: string, rows: object[] }} payload
 */
export function bulkValidateAnnouncements(payload) {
  return request("/api/announcements/bulk-validate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * นำเข้าข้อมูลหลายรายการในครั้งเดียว
 * @param {{ schemaVersion: string, announcementType: string, sourceFileName: string, rows: object[], importedBy?: string }} payload
 */
export function bulkImportAnnouncements(payload) {
  return request("/api/announcements/bulk-import", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * ตรวจสอบสถานะ import job
 * @param {string} jobId
 */
export function getImportJob(jobId) {
  return request(`/api/announcements/import-jobs/${encodeURIComponent(jobId)}`);
}
